// src/components/DocumentsModal.jsx
// Modal de gestion des documents joints à un bien (DPE, diagnostics, plans, photos).
// v2 : "Ajouter" au lieu de "Téléverser", multi-catégories par doc, analyse IA via Claude API.
// Storage : Supabase bucket 'property-documents' (privé, 10MB max, PDF + images)
// Path : {user_id}/{maison_id}/{timestamp}-{filename}

import React, { useState, useRef } from "react";
import { supabase } from "../supabase";

const BUCKET = "property-documents";
const MAX_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIMES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

const DOC_CATEGORIES = [
  { value: "dpe", label: "⚡ DPE", color: "#a78bfa" },
  { value: "diagnostic", label: "🔧 Diagnostic technique", color: "#60a5fa" },
  { value: "copropriete", label: "🏢 Doc copropriété", color: "#fb923c" },
  { value: "plan", label: "📐 Plan / Cadastre", color: "#34d399" },
  { value: "photo", label: "📷 Photo", color: "#fbbf24" },
  { value: "autre", label: "📄 Autre", color: "#9ca3af" },
];

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function getCategoryMeta(value) {
  return DOC_CATEGORIES.find((c) => c.value === value) || DOC_CATEGORIES[DOC_CATEGORIES.length - 1];
}

// Backward compat : ancien doc avait `category` (string), nouveau a `categories` (array).
function getDocCategories(doc) {
  if (Array.isArray(doc.categories) && doc.categories.length > 0) return doc.categories;
  if (typeof doc.category === "string" && doc.category) return [doc.category];
  return ["autre"];
}

function fileIcon(mime) {
  if (!mime) return "📄";
  if (mime.startsWith("image/")) return "🖼️";
  if (mime === "application/pdf") return "📕";
  return "📄";
}

// Auto-détection des catégories depuis le nom de fichier. Renvoie [] si rien
// ne matche : l'utilisateur devra alors sélectionner manuellement.
function detectCategories(filename, mimeType) {
  const lower = (filename || "").toLowerCase();
  const cats = [];
  if (/\bdpe\b/.test(lower)) cats.push("dpe");
  if (/diag|amiante|plomb|termite|gaz|elec|electrique/.test(lower)) cats.push("diagnostic");
  if (/\bag\b|copro|règlement|reglement|assemblee/.test(lower)) cats.push("copropriete");
  if (/plan|cadastre/.test(lower)) cats.push("plan");
  if (cats.length === 0 && mimeType && mimeType.startsWith("image/")) cats.push("photo");
  return cats; // peut être vide → le bouton "Ajouter" sera désactivé
}

// === Composant principal ===

export function DocumentsModal({ maison, user, onSave, onClose }) {
  const [docs, setDocs] = useState(Array.isArray(maison.documents) ? maison.documents : []);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingCategories, setPendingCategories] = useState([]);
  const [pendingName, setPendingName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef();

  const persist = (newDocs) => {
    setDocs(newDocs);
    onSave?.({ ...maison, documents: newDocs });
  };

  const validateFile = (file) => {
    if (!file) return "Aucun fichier sélectionné.";
    if (file.size > MAX_SIZE_BYTES) return `Fichier trop volumineux (${fmtSize(file.size)}). Limite : 10 Mo.`;
    if (!ALLOWED_MIMES.includes(file.type)) return `Type non autorisé (${file.type || "inconnu"}). Accepté : PDF, JPG, PNG, WebP.`;
    return null;
  };

  const handleFileSelect = (file) => {
    setError(null);
    const err = validateFile(file);
    if (err) { setError(err); return; }
    setPendingFile(file);
    setPendingName(file.name);
    setPendingCategories(detectCategories(file.name, file.type));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const togglePendingCategory = (catValue) => {
    if (pendingCategories.includes(catValue)) {
      setPendingCategories(pendingCategories.filter(c => c !== catValue));
    } else {
      setPendingCategories([...pendingCategories, catValue]);
    }
  };

  const handleUpload = async () => {
    if (!pendingFile || !user || !maison.id) return;
    setUploading(true);
    setError(null);
    try {
      const safeName = pendingFile.name.replace(/[^\w.-]/g, "_");
      const path = `${user.id}/${maison.id}/${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(path, pendingFile, { contentType: pendingFile.type, upsert: false });
      if (uploadError) throw uploadError;

      const newDoc = {
        id: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: pendingName || pendingFile.name,
        categories: [...pendingCategories],
        size: pendingFile.size,
        mimeType: pendingFile.type,
        path,
        uploadedAt: new Date().toISOString(),
      };
      persist([...docs, newDoc]);
      setPendingFile(null);
      setPendingName("");
      setPendingCategories([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      console.error("[DocumentsModal] upload error:", e);
      setError(`Erreur upload : ${e.message || "inconnue"}`);
    } finally {
      setUploading(false);
    }
  };

  const handleOpen = async (doc) => {
    setError(null);
    try {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(doc.path, 120);
      if (error) throw error;
      if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("[DocumentsModal] signedUrl error:", e);
      setError(`Erreur ouverture : ${e.message || "inconnue"}`);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.name}" ? Cette action est définitive.`)) return;
    setError(null);
    try {
      const { error: rmError } = await supabase.storage.from(BUCKET).remove([doc.path]);
      if (rmError) throw rmError;
      persist(docs.filter((d) => d.id !== doc.id));
    } catch (e) {
      console.error("[DocumentsModal] delete error:", e);
      setError(`Erreur suppression : ${e.message || "inconnue"}`);
    }
  };

  const handleUpdate = (docId, patch) => {
    persist(docs.map((d) => (d.id === docId ? { ...d, ...patch } : d)));
  };

  // Analyse IA d'un document. forUsage = "residence" | "investissement"
  const handleAnalyze = async (doc, forUsage) => {
    setError(null);
    persist(docs.map(d => d.id === doc.id ? { ...d, _analyzing: true } : d));
    try {
      const { data: urlData, error: urlError } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(doc.path, 300);
      if (urlError) throw urlError;
      if (!urlData?.signedUrl) throw new Error("Pas d'URL signée");

      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error("Session expirée, reconnecte-toi puis réessaie.");

      const resp = await fetch("/api/analyze-document", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}` },
        body: JSON.stringify({
          signedUrl: urlData.signedUrl,
          categories: getDocCategories(doc),
          mimeType: doc.mimeType,
          usage: forUsage,
        }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`);

      const aiAnalysis = {
        result: json.result,
        analyzedForUsage: json.analyzedForUsage || forUsage,
        analyzedAt: new Date().toISOString(),
        model: json.model || "claude-haiku-4-5",
        usage: json.usage || null,
      };
      persist(docs.map(d => d.id === doc.id ? { ...d, aiAnalysis, _analyzing: false } : d));
    } catch (e) {
      console.error("[DocumentsModal] analyze error:", e);
      setError(`Erreur analyse : ${e.message || "inconnue"}`);
      persist(docs.map(d => d.id === doc.id ? { ...d, _analyzing: false } : d));
    }
  };

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={modal}>
        <div style={header}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#f1f5f9" }}>📎 Documents</h2>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {maison.titre || `${maison.type || ""} · ${maison.ville || ""}`}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn} aria-label="Fermer">✕</button>
        </div>

        {!pendingFile && (
          <div
            onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{ ...dropZone, borderColor: dragActive ? "#a78bfa" : "rgba(167,139,250,0.3)", background: dragActive ? "rgba(167,139,250,0.1)" : "rgba(255,255,255,0.03)" }}
          >
            <div style={{ fontSize: 28, marginBottom: 6 }}>📤</div>
            <div style={{ fontSize: 13, color: "#c4b5fd", fontWeight: 600 }}>Glisser-déposer un fichier ici</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>ou cliquer pour parcourir · PDF, JPG, PNG, WebP · 10 Mo max</div>
            <input
              ref={fileInputRef} type="file"
              accept=".pdf,image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => e.target.files && e.target.files[0] && handleFileSelect(e.target.files[0])}
              style={{ display: "none" }}
            />
          </div>
        )}

        {pendingFile && (
          <div style={pendingCard}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 24 }}>{fileIcon(pendingFile.type)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 2 }}>Fichier à ajouter</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pendingFile.name}</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{fmtSize(pendingFile.size)} · {pendingFile.type}</div>
              </div>
            </div>

            <label style={{ display: "block", marginBottom: 10 }}>
              <span style={fieldLabel}>Nom d'affichage</span>
              <input
                type="text" value={pendingName} onChange={(e) => setPendingName(e.target.value)}
                placeholder="Nom à afficher dans la liste" style={inputStyle}
              />
            </label>

            <div style={{ marginBottom: 14 }}>
              <span style={fieldLabel}>Catégories (1 ou plusieurs)</span>
              <CategoryPills selected={pendingCategories} onToggle={togglePendingCategory} />
              <div style={{ fontSize: 10, color: "#6b7280", marginTop: 5 }}>
                Astuce : un fichier peut contenir plusieurs documents (ex: DPE + Diagnostic technique).
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { setPendingFile(null); setPendingName(""); setError(null); }} style={btnSecondary} disabled={uploading}>Annuler</button>
              <button onClick={handleUpload} style={btnPrimary} disabled={uploading || !pendingName.trim() || pendingCategories.length === 0}>
                {uploading ? "⏳ Ajout en cours..." : "➕ Ajouter"}
              </button>
            </div>
            {pendingCategories.length === 0 && (
              <div style={{ fontSize: 10, color: "#fbbf24", marginTop: 8, textAlign: "center" }}>
                Sélectionne au moins une catégorie pour pouvoir ajouter.
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.4)", color: "#fca5a5", fontSize: 12 }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
            {docs.length} document{docs.length > 1 ? "s" : ""}
          </div>
          {docs.length === 0 && !pendingFile && (
            <div style={{ padding: "20px", textAlign: "center", color: "#6b7280", fontSize: 13 }}>
              Aucun document attaché pour ce bien.
            </div>
          )}
          {docs.map((doc) => (
            <DocItem
              key={doc.id} doc={doc}
              maisonUsage={maison.usage || "residence"}
              onOpen={() => handleOpen(doc)}
              onDelete={() => handleDelete(doc)}
              onUpdate={(patch) => handleUpdate(doc.id, patch)}
              onAnalyze={(forUsage) => handleAnalyze(doc, forUsage)}
            />
          ))}
        </div>

        <div style={{ marginTop: 16, textAlign: "center" }}>
          <button onClick={onClose} style={btnSecondary}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// === Sous-composants ===

function CategoryPills({ selected, onToggle }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
      {DOC_CATEGORIES.map((c) => {
        const isSelected = selected.includes(c.value);
        return (
          <button
            key={c.value}
            onClick={() => onToggle(c.value)}
            style={{
              padding: "6px 10px", borderRadius: 7,
              border: `1px solid ${isSelected ? c.color : "rgba(255,255,255,0.15)"}`,
              background: isSelected ? c.color + "22" : "transparent",
              color: isSelected ? c.color : "#9ca3af",
              fontWeight: 600, fontSize: 11, cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

function DocItem({ doc, maisonUsage, onOpen, onDelete, onUpdate, onAnalyze }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(doc.name);
  const [categories, setCategories] = useState(getDocCategories(doc));
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [choosingUsage, setChoosingUsage] = useState(false);
  const docCats = getDocCategories(doc);
  const hasAnalysis = !!doc.aiAnalysis?.result;
  const analyzing = !!doc._analyzing;

  // Détermine s'il y a un décalage entre l'usage de l'analyse et l'usage actuel du bien
  const analyzedForUsage = doc.aiAnalysis?.analyzedForUsage;
  const usageMismatch =
    hasAnalysis &&
    maisonUsage !== "mixte" &&
    analyzedForUsage &&
    analyzedForUsage !== maisonUsage;

  // Click sur "Analyser" : si bien mixte → demander le mode, sinon analyser direct
  const handleAnalyzeClick = () => {
    if (maisonUsage === "mixte") {
      setChoosingUsage(true);
    } else {
      onAnalyze(maisonUsage);
    }
  };

  const toggleEditCat = (c) => {
    if (categories.includes(c)) {
      setCategories(categories.filter(x => x !== c));
    } else setCategories([...categories, c]);
  };

  const handleSave = () => {
    if (name.trim() !== doc.name || JSON.stringify(categories) !== JSON.stringify(docCats)) {
      onUpdate({ name: name.trim() || doc.name, categories, category: undefined });
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setName(doc.name);
    setCategories(docCats);
    setEditing(false);
  };

  if (editing) {
    return (
      <div style={docCard}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>{fileIcon(doc.mimeType)}</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, flex: 1 }} autoFocus />
        </div>
        <div style={{ marginBottom: 10 }}>
          <span style={fieldLabel}>Catégories</span>
          <CategoryPills selected={categories} onToggle={toggleEditCat} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={handleCancel} style={{ ...btnSecondary, padding: "6px 10px", fontSize: 11 }}>Annuler</button>
          <button onClick={handleSave} style={{ ...btnPrimary, padding: "6px 10px", fontSize: 11 }} disabled={categories.length === 0}>Sauvegarder</button>
        </div>
        {categories.length === 0 && (
          <div style={{ fontSize: 10, color: "#fbbf24", marginTop: 8 }}>
            Au moins une catégorie est requise.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={docCard}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span style={{ fontSize: 22, flexShrink: 0, lineHeight: 1.2 }}>{fileIcon(doc.mimeType)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 4 }}>{doc.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
            {docCats.map(catValue => {
              const cat = getCategoryMeta(catValue);
              return (
                <span key={catValue} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 5, background: cat.color + "22", color: cat.color, border: `1px solid ${cat.color}55`, fontWeight: 600 }}>
                  {cat.label}
                </span>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>
            {fmtSize(doc.size)} · {fmtDate(doc.uploadedAt)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button onClick={() => setEditing(true)} style={iconBtn} title="Modifier">✏️</button>
          <button onClick={onOpen} style={iconBtn} title="Ouvrir / Télécharger">👁</button>
          <button onClick={onDelete} style={{ ...iconBtn, borderColor: "rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.07)", color: "#f87171" }} title="Supprimer">🗑</button>
        </div>
      </div>

      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        {/* Popup choix d'usage pour bien mixte */}
        {choosingUsage && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6, textAlign: "center" }}>Analyser ce document pour :</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setChoosingUsage(false); onAnalyze("residence"); }} style={{ ...analyzeBtn, flex: 1, borderColor: "rgba(96,165,250,0.4)", color: "#93c5fd" }}>🏠 Résidence principale</button>
              <button onClick={() => { setChoosingUsage(false); onAnalyze("investissement"); }} style={{ ...analyzeBtn, flex: 1 }}>💰 Investissement</button>
            </div>
            <button onClick={() => setChoosingUsage(false)} style={{ ...miniBtn, marginTop: 6, width: "100%" }}>Annuler</button>
          </div>
        )}

        {!hasAnalysis && !analyzing && !choosingUsage && (
          <button onClick={handleAnalyzeClick} style={analyzeBtn}>🤖 Analyser avec l'IA</button>
        )}
        {analyzing && (
          <div style={{ fontSize: 12, color: "#a78bfa", fontWeight: 600, textAlign: "center", padding: "8px" }}>
            ⏳ Analyse en cours… (peut prendre 5-15 secondes)
          </div>
        )}
        {hasAnalysis && !analyzing && (
          <>
            {usageMismatch && (
              <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 7, background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.4)" }}>
                <div style={{ fontSize: 11, color: "#fcd34d", marginBottom: 6 }}>
                  ⓘ Analyse faite pour <b>{analyzedForUsage === "investissement" ? "investissement" : "résidence principale"}</b>, mais ce bien est maintenant marqué <b>{maisonUsage === "investissement" ? "investissement" : "résidence principale"}</b>.
                </div>
                <button onClick={() => onAnalyze(maisonUsage)} style={{ ...analyzeBtn, fontSize: 11, padding: "7px" }}>
                  🔄 Réanalyser pour {maisonUsage === "investissement" ? "investissement" : "résidence principale"}
                </button>
              </div>
            )}
            <AnalysisDisplay
              analysis={doc.aiAnalysis}
              showDetails={showAnalysis}
              onToggle={() => setShowAnalysis(s => !s)}
              onReanalyze={handleAnalyzeClick}
            />
          </>
        )}
      </div>
    </div>
  );
}

function AnalysisDisplay({ analysis, showDetails, onToggle, onReanalyze }) {
  const result = analysis.result || {};
  const analyzedForUsage = analysis.analyzedForUsage;
  const hasAlertes = Array.isArray(result.alertes) && result.alertes.length > 0;
  const keyFields = result.keyFields || {};
  const filledFields = Object.entries(keyFields).filter(([k, v]) => v !== null && v !== undefined && v !== "");

  const rawRecos = result.recommandations;
  let urgentes = [];
  let energetiques = [];
  if (Array.isArray(rawRecos)) {
    urgentes = [];
    energetiques = rawRecos.map(r => ({ action: typeof r === "string" ? r : (r.action || ""), impactDpe: null }));
  } else if (rawRecos && typeof rawRecos === "object") {
    urgentes = Array.isArray(rawRecos.urgentes) ? rawRecos.urgentes : [];
    energetiques = (Array.isArray(rawRecos.energetiques) ? rawRecos.energetiques : []).map(r =>
      typeof r === "string" ? { action: r, impactDpe: null } : { action: r.action || "", impactDpe: r.impactDpe || null }
    );
  }
  const hasUrgentes = urgentes.length > 0;
  const hasEnergetiques = energetiques.length > 0;
  const hasAnyReco = hasUrgentes || hasEnergetiques;
  const isOldFormat = Array.isArray(rawRecos);

  // Contexte spécifique selon l'usage
  const ctx = result.contextSpecifique || {};
  const usageLabel = analyzedForUsage === "investissement" ? "💰 Analyse pour investissement locatif" : "📊 Analyse pour résidence principale";
  const usageColor = analyzedForUsage === "investissement" ? "#fbbf24" : "#60a5fa";

  return (
    <div>
      {/* Badge d'usage */}
      {analyzedForUsage && (
        <div style={{ display: "inline-block", fontSize: 10, fontWeight: 700, color: usageColor, background: usageColor + "1a", border: `1px solid ${usageColor}44`, borderRadius: 6, padding: "3px 9px", marginBottom: 8 }}>
          {usageLabel}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: "#a78bfa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          🤖 {result.type || "Analyse IA"}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={onToggle} style={{ ...miniBtn, color: "#c4b5fd" }}>
            {showDetails ? "Masquer" : "Détails"}
          </button>
          <button onClick={onReanalyze} style={miniBtn} title="Réanalyser">🔄</button>
        </div>
      </div>

      {result.summary && (
        <div style={{ fontSize: 12, color: "#d1d5db", lineHeight: 1.5, marginBottom: 6 }}>{result.summary}</div>
      )}

      {hasAlertes && (
        <div style={{ marginBottom: 6 }}>
          {result.alertes.map((a, i) => (
            <div key={i} style={alerteStyle}>⚠ {a}</div>
          ))}
        </div>
      )}

      {showDetails && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          {filledFields.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Champs extraits</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 6 }}>
                {filledFields.map(([k, v]) => (
                  <div key={k} style={{ fontSize: 11, padding: "5px 8px", background: "rgba(255,255,255,0.04)", borderRadius: 6 }}>
                    <span style={{ color: "#9ca3af" }}>{labelizeKey(k)}</span>
                    <span style={{ color: "#fff", fontWeight: 600, marginLeft: 4 }}>{formatValue(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasUrgentes && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: "#fca5a5", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                🚨 Interventions nécessaires
              </div>
              {urgentes.map((r, i) => (
                <div key={i} style={urgentRecoStyle}>→ {r}</div>
              ))}
            </div>
          )}

          {hasEnergetiques && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: "#fcd34d", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                🏠 Améliorations énergétiques {isOldFormat && <span style={{ color: "#6b7280", fontWeight: 500 }}>(analyse ancienne — réanalyse pour voir la séparation)</span>}
              </div>
              {energetiques.map((r, i) => (
                <div key={i} style={energReco}>
                  <div style={{ fontSize: 11, color: "#fcd34d" }}>→ {r.action}</div>
                  {r.impactDpe && (
                    <div style={{ fontSize: 10, color: "#a78bfa", marginTop: 3, paddingLeft: 12 }}>
                      📊 Impact DPE : <span style={{ fontWeight: 600 }}>{r.impactDpe}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Contexte spécifique selon l'usage */}
          <ContextSpecifiqueBlock ctx={ctx} usage={analyzedForUsage} />

          {!hasAnyReco && (
            <div style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic", padding: "8px 0" }}>
              Aucune recommandation extraite par l'IA.
            </div>
          )}

          {result._parseError && (
            <div style={{ marginTop: 8, fontSize: 10, color: "#fbbf24", fontStyle: "italic" }}>
              ⓘ Le résultat n'a pas pu être parsé en structure complète. Texte brut affiché dans le résumé.
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 10, color: "#6b7280" }}>
            Analysé le {fmtDate(analysis.analyzedAt)} · {analysis.model}
          </div>
        </div>
      )}
    </div>
  );
}

// Affichage du contexte spécifique selon l'usage (RP ou Invest)
function ContextSpecifiqueBlock({ ctx, usage }) {
  if (!ctx || Object.keys(ctx).length === 0) return null;
  const isInvest = usage === "investissement";

  const renderText = (label, value) => {
    if (!value) return null;
    return (
      <div style={{ fontSize: 11, marginBottom: 6 }}>
        <span style={{ color: "#9ca3af", fontWeight: 600 }}>{label} : </span>
        <span style={{ color: "#e5e7eb" }}>{value}</span>
      </div>
    );
  };
  const renderList = (label, arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    return (
      <div style={{ fontSize: 11, marginBottom: 6 }}>
        <span style={{ color: "#9ca3af", fontWeight: 600 }}>{label} :</span>
        {arr.map((item, i) => <div key={i} style={{ color: "#e5e7eb", paddingLeft: 10, marginTop: 2 }}>• {item}</div>)}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: 12, padding: "10px 12px", background: isInvest ? "rgba(251,191,36,0.06)" : "rgba(96,165,250,0.06)", border: `1px solid ${isInvest ? "rgba(251,191,36,0.2)" : "rgba(96,165,250,0.2)"}`, borderRadius: 8 }}>
      <div style={{ fontSize: 10, color: isInvest ? "#fbbf24" : "#60a5fa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
        {isInvest ? "💰 Contexte investissement" : "🏠 Contexte résidence principale"}
      </div>
      {isInvest ? (
        <>
          {renderText("Risque loi climat", ctx.risqueLoiClimat)}
          {renderText("Impact sur le loyer", ctx.impactLoyer)}
          {renderText("Charges récupérables", ctx.chargesRecuperables)}
          {renderList("Travaux avant mise en location", ctx.travauxAvantMiseEnLocation)}
          {renderList("Aides bailleur", ctx.aidesBailleur)}
        </>
      ) : (
        <>
          {renderText("Coût énergétique annuel", ctx.coutEnergetiqueAnnuel)}
          {renderText("Bilan confort", ctx.confortBilan)}
          {renderList("Travaux avant emménagement", ctx.travauxAvantEmmenagement)}
          {renderList("Aides propriétaire occupant", ctx.aidesProprioOccupant)}
        </>
      )}
    </div>
  );
}

const KEY_LABELS = {
  classeEnergie: "Classe énergie", consommationKwh: "Consommation", classeGes: "Classe GES",
  emissionsGes: "Émissions GES", dateDiagnostic: "Date diagnostic", validiteJusquA: "Valide jusqu'à",
  surfaceM2: "Surface", coutAnnuelMin: "Coût min /an", coutAnnuelMax: "Coût max /an",
  presenceAmiante: "Amiante", presencePlomb: "Plomb", etatElectricite: "Électricité",
  etatGaz: "Gaz", presenceTermites: "Termites",
};

function labelizeKey(k) { return KEY_LABELS[k] || k; }

function formatValue(v) {
  if (typeof v === "boolean") return v ? "Oui" : "Non";
  if (typeof v === "number") return v.toLocaleString("fr-FR");
  if (typeof v === "string") return v;
  return String(v);
}

// === Styles ===

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 2500,
  display: "flex", alignItems: "center", justifyContent: "center", padding: 12,
};
const modal = {
  background: "#0f0f13", color: "#f1f5f9", borderRadius: 18,
  border: "1px solid rgba(255,255,255,0.12)",
  padding: 22, maxWidth: 680, width: "100%", maxHeight: "92vh", overflow: "auto",
};
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12 };
const closeBtn = {
  background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
  width: 30, height: 30, borderRadius: "50%", fontSize: 14, cursor: "pointer", flexShrink: 0,
};
const dropZone = {
  padding: 28, borderRadius: 14, border: "2px dashed",
  textAlign: "center", cursor: "pointer", transition: "all 0.2s",
};
const pendingCard = {
  padding: 16, borderRadius: 12,
  background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.3)",
};
const docCard = {
  padding: 12, borderRadius: 10,
  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
  marginBottom: 8,
};
const fieldLabel = {
  display: "block", fontSize: 11, color: "#9ca3af",
  fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4,
};
const inputStyle = {
  width: "100%", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8, padding: "9px 11px", fontSize: 13, color: "#fff", outline: "none", boxSizing: "border-box",
};
const btnSecondary = {
  padding: "9px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)",
  background: "transparent", color: "#d1d5db",
  fontWeight: 600, cursor: "pointer", fontSize: 12,
};
const btnPrimary = {
  padding: "9px 16px", borderRadius: 8, border: "none",
  background: "#a78bfa", color: "#0f0f13", fontWeight: 700, cursor: "pointer", fontSize: 13, flex: 1,
};
const iconBtn = {
  padding: "6px 9px", borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.15)", background: "transparent",
  color: "#9ca3af", cursor: "pointer", fontSize: 12,
};
const analyzeBtn = {
  width: "100%", padding: "9px", borderRadius: 7,
  border: "1px solid rgba(167,139,250,0.4)", background: "rgba(167,139,250,0.08)",
  color: "#c4b5fd", fontWeight: 600, fontSize: 12, cursor: "pointer",
};
const miniBtn = {
  padding: "3px 8px", borderRadius: 5,
  border: "1px solid rgba(255,255,255,0.15)", background: "transparent",
  color: "#9ca3af", fontSize: 10, cursor: "pointer", fontWeight: 600,
};
const alerteStyle = {
  fontSize: 11, color: "#fca5a5", padding: "5px 8px",
  background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
  borderRadius: 6, marginBottom: 4,
};
const urgentRecoStyle = {
  fontSize: 11, color: "#fca5a5", padding: "6px 9px",
  background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)",
  borderRadius: 6, marginBottom: 4,
};
const energReco = {
  fontSize: 11, color: "#fcd34d", padding: "6px 9px",
  background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)",
  borderRadius: 6, marginBottom: 4,
};
const recoStyle = {
  fontSize: 11, color: "#fcd34d", padding: "5px 8px",
  background: "rgba(245,158,11,0.06)", borderRadius: 6, marginBottom: 4,
};
